/**
 * API Service - Main API client for application operations
 * 
 * This service handles all non-authentication API calls including file uploads,
 * analysis requests, and results retrieval with proper error handling.
 * 
 * Features:
 * - Automatic access token attachment from AuthContext
 * - Automatic token refresh on 401 errors
 * - Cookie support (withCredentials) for refresh tokens
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import env from '../config/env'
import { getAccessToken, refreshAccessToken } from './tokenManager'

// API configuration
// Always use env.API_URL which handles localhost correctly (uses current origin)
// Fallback to window.location.origin only if env.API_URL is not set
const API_BASE_URL = env.API_URL || (typeof window !== 'undefined' ? window.location.origin : '')
const API_TIMEOUT = 600000 // 10 minutes for large file uploads (2GB)

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: API_TIMEOUT,
  withCredentials: true, // Required for HttpOnly cookies (refresh tokens)
  headers: {
    'Content-Type': 'application/json',
  },
})

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (error?: unknown) => void
  config: InternalAxiosRequestConfig
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
    } else {
      // Retry the request with new token
      if (config.headers && token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      resolve(apiClient(config))
    }
  })
  
  failedQueue = []
}

// Request interceptor: Attach access token to all requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // For FormData uploads, remove Content-Type header to let browser set it with boundary
    // The default 'application/json' header interferes with multipart/form-data
    if (config.data instanceof FormData && config.headers) {
      // Delete Content-Type header - browser will set it automatically with boundary
      delete config.headers['Content-Type']
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor: Handle 401 errors with automatic token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    
    // If error is 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }
      
      originalRequest._retry = true
      isRefreshing = true
      
      try {
        // Try to refresh the token
        await refreshAccessToken()
        const newToken = getAccessToken()
        
        // Process queued requests
        processQueue(null, newToken)
        
        // Retry original request with new token
        if (originalRequest.headers && newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }
        
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed - clear queue and reject all requests
        processQueue(refreshError, null)
        
        // Redirect to login if refresh fails
        if (typeof window !== 'undefined') {
          window.location.href = '/login'
        }
        
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    
    // For other errors, just throw
    return Promise.reject(error)
  }
)

// Types for API responses
export interface UploadResponse {
  id: string
  filename: string
  size: number
  uploadedAt: string
  status: string
}

export interface AnalysisRequest {
  upload_ids: number[]
  task_type: string
  model_type: string
  thinking_budget?: number
  include_thoughts?: boolean
  parameters: {
    resolution?: string
    thinkingBudget?: number
    includeThoughts?: boolean
    task_description?: string
    media_resolution?: string
  }
}

export interface AnalysisResponse {
  id: string
  task_id: number
  task_type: string
  status: string
  created_time: string
  completed_time?: string
  result_path?: string
  results?: unknown
  error?: string
  createdAt: string
  completedAt?: string
}

// Internal shape for analysis result; exported below as a type alias
interface AnalysisResultShape {
  id: string
  analysis_text: string
  metadata: {
    model: string
    tokens: number
    processingTime: number
    timestamp: string
  }
  artifacts: unknown[]
}

// Explicitly export a type alias so consumers can reliably `import type { AnalysisResult }`
export type AnalysisResult = AnalysisResultShape

export interface AnalysisResultItem {
  id: string
  task_id: number
  task_type: string
  filename: string
  status: string
  created_time: string
  completed_time?: string
  results?: AnalysisResult[]
  createdAt: string
  completedAt?: string
}

// Backward-compatible alias: previously some code referenced AnalysisResults
// as an array type. Provide an alias to preserve compatibility with any
// remaining references and enable indexed access.
export type AnalysisResults = AnalysisResultItem[]

// Search types
export interface SearchResult {
  result_id: string
  filename: string
  scene_index: number
  start_time: string
  end_time: string
  visual_description: string
  audio_description: string | null
  match_score: number
  matched_text: string
  created_time: string
}

export interface SearchResponse {
  query: string
  total_results: number
  results: SearchResult[]
  mode_used: string  // Which search mode produced results: phrase, websearch, or plain
}

// File upload service
export const uploadService = {
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData()
    formData.append('files', file)

    const response = await apiClient.post<UploadResponse>('/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data
  },

  async getUploadStatus(uploadId: string): Promise<UploadResponse> {
    const response = await apiClient.get<UploadResponse>(`/upload/${uploadId}`)
    return response.data
  }
}

// Analysis service
export const analysisService = {
  async startAnalysis(request: AnalysisRequest): Promise<AnalysisResponse> {
    const response = await apiClient.post<AnalysisResponse>('/analysis/start', request)
    return response.data
  },

  async getAnalysisStatus(analysisId: string): Promise<AnalysisResponse> {
    const response = await apiClient.get<AnalysisResponse>(`/analysis/${analysisId}`)
    return response.data
  },

  async getAnalysisResult(analysisId: string): Promise<AnalysisResult> {
    const response = await apiClient.get<AnalysisResult>(`/analysis/${analysisId}/results`)
    return response.data
  },

  async getAnalysisHistory(): Promise<AnalysisResponse[]> {
    const response = await apiClient.get<AnalysisResponse[]>('/analysis/tasks')
    return response.data
  }
}

// Results service
export const resultsService = {
  async getResults(): Promise<AnalysisResultItem[]> {
    const response = await apiClient.get<AnalysisResultItem[]>('/results')
    return response.data
  },

  async downloadResult(analysisId: string, format: 'json' | 'premiere-xml' | 'zip' = 'json'): Promise<Blob> {
    const response = await apiClient.get(`/results/download/${analysisId}`, {
      params: { format },
      responseType: 'blob'
    })
    return response.data
  },

  async downloadResultXML(analysisId: string): Promise<Blob> {
    // Backwards compatibility wrapper - use the unified download endpoint
    return this.downloadResult(analysisId, 'premiere-xml')
  },

  async downloadResultRichXML(analysisId: string): Promise<Blob> {
    const response = await apiClient.get(`/results/download-rich-xml/${analysisId}`, {
      responseType: 'blob'
    })
    return response.data
  },

  async downloadResultOrganizedXML(
    analysisId: string,
    organization: 'scene_type' | 'quality' | 'production' | 'flat' = 'scene_type'
  ): Promise<Blob> {
    const response = await apiClient.get(`/results/download-organized-xml/${analysisId}`, {
      params: { organization },
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Download structured XML with category bins and scene sequences.
   * Each scene becomes its own sequence with timecode-prefixed names.
   */
  async downloadResultStructuredXML(analysisId: string): Promise<Blob> {
    const response = await apiClient.get(`/results/download-structured-xml/${analysisId}`, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Export multiple videos as a single project XML with folder hierarchy.
   * Preserves original folder structure as nested bins.
   */
  async exportProjectXML(taskIds: number[], projectName: string = 'Project'): Promise<Blob> {
    const response = await apiClient.post('/results/export/project',
      { task_ids: taskIds, project_name: projectName },
      { responseType: 'blob' }
    )
    return response.data
  },

  async deleteResult(analysisId: string): Promise<void> {
    await apiClient.delete(`/results/${analysisId}`)
  }
}

// Search service
export const searchService = {
  async search(query: string, limit: number = 100): Promise<SearchResponse> {
    const response = await apiClient.get<SearchResponse>('/search', {
      params: { q: query, limit }
    })
    return response.data
  }
}

// Export the main API client for custom requests
export { apiClient }

// Export individual functions for backward compatibility
export const uploadFiles = uploadService.uploadFile
export const startAnalysis = analysisService.startAnalysis
export const getAllAnalysisTasks = analysisService.getAnalysisHistory

// Additional exports for ResultsPage compatibility
export const getAnalysisResults = resultsService.getResults
export const getAnalysisResultItems = resultsService.getResults
export const downloadResults = resultsService.downloadResult
export const listResults = analysisService.getAnalysisHistory

// Export search function
export const searchScenes = searchService.search

// AI provider discovery service
export const aiService = {
  async getProviders() {
    const response = await apiClient.get('/ai/providers')
    return response.data
  },

  async getModels(provider: string) {
    const response = await apiClient.get(`/ai/providers/${provider}/models`)
    return response.data
  },
}

// Export default API service object
export default {
  upload: uploadService,
  analysis: analysisService,
  results: resultsService,
  ai: aiService,
  // Direct access to apiClient methods for backward compatibility
  get: (url: string, config?: AxiosRequestConfig) => apiClient.get(url, config),
  post: (url: string, data?: unknown, config?: AxiosRequestConfig) => apiClient.post(url, data, config),
  put: (url: string, data?: unknown, config?: AxiosRequestConfig) => apiClient.put(url, data, config),
  patch: (url: string, data?: unknown, config?: AxiosRequestConfig) => apiClient.patch(url, data, config),
  delete: (url: string, config?: AxiosRequestConfig) => apiClient.delete(url, config)
}
